import React, { useEffect, useState, useCallback } from 'react';
import { API_URL } from '../../config';
import { 
  Phone, 
  Mail, 
  Calendar, 
  Trash2, 
  ExternalLink, 
  ChevronRight, 
  ChevronLeft,
  X, 
  User, 
  Car, 
  MapPin, 
  DollarSign, 
  Zap, 
  Activity,
  CheckCircle2,
  Clock,
  AlertCircle,
  Image as ImageIcon,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../Context/AuthContext';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

const COLOR_MAP = { original: '#1e293b', local_painted: '#f97316', painted: '#3b82f6', changed: '#ef4444' };

const SellRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const { token } = useAuth();
  const location = useLocation();

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/sell-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
        
        // Auto open details if state has openId
        if (location.state?.openId) {
          const target = data.find(r => r.id === location.state.openId);
          if (target) {
            setSelectedRequest(target);
            setIsModalOpen(true);
          }
        }
      }
    } catch (err) {
      console.error('Talepler yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [token, location.state]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Pagination Logic
  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRequests = requests.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/admin/sell-requests/${id}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setRequests(prev => prev.map(req => req.id === id ? { ...req, status: newStatus } : req));
        if (selectedRequest && selectedRequest.id === id) {
          setSelectedRequest({ ...selectedRequest, status: newStatus });
        }
        Swal.fire({
          icon: 'success',
          title: 'Durum Güncellendi',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          background: '#0f172a',
          color: '#fff'
        });
      }
    } catch (err) {
      console.error('Durum güncelleme hatası:', err);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Emin misiniz?',
      text: "Bu talebi silmek istiyor musunuz?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#334155',
      confirmButtonText: 'Evet, Sil!',
      background: '#0f172a',
      color: '#fff'
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_URL}/admin/quotes/${id}?source=sell_request`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          Swal.fire({ icon: 'success', title: 'Silindi!', background: '#0f172a', color: '#fff', timer: 1500 });
          fetchRequests();
          if (selectedRequest && selectedRequest.id === id) setIsModalOpen(false);
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Hata!', background: '#0f172a', color: '#fff' });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Bekliyor': return 'badge-warning';
      case 'İnceleniyor': return 'badge-info';
      case 'Teklif Verildi': return 'badge-success';
      case 'Satın Alındı': return 'badge-primary';
      case 'Reddedildi': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  const openDetails = (req) => {
    setSelectedRequest(req);
    setIsModalOpen(true);
  };

  if (loading && requests.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-dim)' }}>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="admin-sell-requests">
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="admin-card-title" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', marginBottom: '0.4rem' }}>Araç Satım Talepleri</h1>
          <p style={{ color: 'var(--text-dim)', fontSize: '1rem' }}>Müşterilerden gelen "Aracımı Sat" başvurularını detaylıca yönetin. ({requests.length} Talep)</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
           <div className="stat-pill" style={{ background: 'rgba(255,255,255,0.03)', padding: '0.5rem 1rem', borderRadius: '0.75rem', border: '1px solid var(--navbar-border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} color="#f59e0b" />
              <span style={{ fontSize: '0.85rem', fontWeight: '700' }}>{requests.filter(r => r.status === 'Bekliyor').length} Bekleyen</span>
           </div>
        </div>
      </div>

      <div className="admin-card" style={{ borderRadius: '1.5rem', border: '1px solid var(--navbar-border)', padding: 0 }}>
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>BAŞVURAN</th>
                <th>ARAÇ BİLGİSİ</th>
                <th>BEKLENEN FİYAT</th>
                <th>DURUM</th>
                <th>TARİH</th>
                <th style={{ textAlign: 'right' }}>İŞLEMLER</th>
              </tr>
            </thead>
            <tbody>
              {currentRequests.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '4rem', textAlign: 'center', color: 'var(--text-dim)' }}>Başvuru bulunmuyor.</td>
                </tr>
              ) : (
                currentRequests.map((req) => (
                  <tr key={req.id}>
                    <td>
                      <div style={{ fontWeight: '700' }}>{req.name} {req.surname}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.2rem' }}>{req.phone}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '45px', height: '32px', background: 'var(--bg-secondary)', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--navbar-border)' }}>
                          {req.images && req.images.length > 0 ? (
                            <img src={req.images[0]} alt="car" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ImageIcon size={14} color="var(--text-dim)" /></div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600' }}>{req.brand} {req.model}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>{req.year} • {req.mileage?.toLocaleString('tr-TR')} KM</div>
                        </div>
                      </div>
                    </td>
                    <td>
                       <div style={{ color: 'var(--primary)', fontWeight: '800' }}>
                          {req.expected_price ? `₺${req.expected_price}` : 'Belirtilmedi'}
                       </div>
                    </td>
                    <td>
                      <select 
                        className={`status-select ${getStatusColor(req.status)}`}
                        value={req.status}
                        onChange={(e) => handleStatusUpdate(req.id, e.target.value)}
                        style={{ 
                          background: 'rgba(255,255,255,0.05)', 
                          border: '1px solid rgba(255,255,255,0.1)', 
                          borderRadius: '0.5rem', 
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          color: 'inherit',
                          cursor: 'pointer'
                        }}
                      >
                        {['Bekliyor', 'İnceleniyor', 'Teklif Verildi', 'Satın Alındı', 'Reddedildi'].map(s => (
                          <option key={s} value={s} style={{ background: '#0f172a' }}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>{new Date(req.created_at).toLocaleDateString()}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                        <button onClick={() => openDetails(req)} className="btn-icon" title="Detaylar" style={{ background: 'rgba(0, 209, 255, 0.05)', color: 'var(--primary)' }}>
                          <ExternalLink size={18} />
                        </button>
                        <button onClick={() => handleDelete(req.id)} className="btn-icon" title="Sil" style={{ background: 'rgba(239, 68, 68, 0.05)', color: '#f87171' }}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '2rem', paddingBottom: '2.5rem', flexWrap: 'wrap' }}>
          <button 
            disabled={currentPage === 1}
            onClick={() => paginate(currentPage - 1)}
            style={{ 
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
              color: currentPage === 1 ? '#475569' : 'var(--primary)', 
              padding: '0.6rem 1.2rem', borderRadius: '0.75rem', 
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.85rem' 
            }}
          >
            <ChevronLeft size={18} />
          </button>
          
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => paginate(i + 1)}
                style={{
                  width: '40px', height: '40px', borderRadius: '0.75rem', border: '1px solid',
                  borderColor: currentPage === i + 1 ? 'var(--primary)' : 'rgba(255,255,255,0.1)',
                  background: currentPage === i + 1 ? 'var(--primary)' : 'rgba(255,255,255,0.05)',
                  color: currentPage === i + 1 ? '#000' : 'white',
                  fontWeight: '800', cursor: 'pointer', transition: 'all 0.2s',
                  boxShadow: currentPage === i + 1 ? '0 0 15px rgba(0, 209, 255, 0.3)' : 'none'
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => paginate(currentPage + 1)}
            style={{ 
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
              color: currentPage === totalPages ? '#475569' : 'var(--primary)', 
              padding: '0.6rem 1.2rem', borderRadius: '0.75rem', 
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.85rem' 
            }}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* DETAIL MODAL */}
      {isModalOpen && selectedRequest && (
        <div className="detail-modal-overlay">
          <div className="detail-modal-content">
            <div className="detail-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                 <div className="header-icon-box">
                    <Zap size={24} color="var(--primary)" />
                 </div>
                 <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: 'white' }}>Talep Detayları</h2>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>ID: #{selectedRequest.id} • Başvuru Tarihi: {new Date(selectedRequest.created_at).toLocaleString()}</p>
                 </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="close-btn"><X size={24} /></button>
            </div>

            <div className="detail-modal-body">
               <div className="detail-grid">
                  {/* Left Column: Info */}
                  <div className="detail-col">
                     <div className="detail-section">
                        <div className="section-title"><User size={16} /> Müşteri Bilgileri</div>
                        <div className="info-card">
                           <div className="info-row"><span>Ad Soyad</span><strong>{selectedRequest.name} {selectedRequest.surname}</strong></div>
                           <div className="info-row"><span>Telefon</span><strong>{selectedRequest.phone}</strong></div>
                           <div className="info-row"><span>E-Posta</span><strong>{selectedRequest.email || '-'}</strong></div>
                        </div>
                     </div>

                     <div className="detail-section">
                        <div className="section-title"><Car size={16} /> Teknik Özellikler</div>
                        <div className="info-card">
                           <div className="info-row"><span>Marka / Model</span><strong>{selectedRequest.brand} / {selectedRequest.model}</strong></div>
                           <div className="info-row"><span>Yıl</span><strong>{selectedRequest.year}</strong></div>
                           <div className="info-row"><span>Kilometre</span><strong>{selectedRequest.mileage.toLocaleString()} KM</strong></div>
                           <div className="info-row"><span>Yakıt / Vites</span><strong>{selectedRequest.fuel_type} / {selectedRequest.transmission}</strong></div>
                           <div className="info-row"><span>Beklenen Fiyat</span><strong style={{ color: 'var(--primary)' }}>₺{selectedRequest.expected_price || '-'}</strong></div>
                        </div>
                     </div>

                     <div className="detail-section">
                        <div className="section-title"><Activity size={16} /> Müşteri Notu</div>
                        <div className="info-card note-card">
                           {selectedRequest.notes || 'Not bırakılmamış.'}
                        </div>
                     </div>
                  </div>

                  {/* Right Column: Visuals */}
                  <div className="detail-col">
                     <div className="detail-section">
                        <div className="section-title"><ImageIcon size={16} /> Fotoğraflar ({selectedRequest.images?.length || 0})</div>
                        <div className="image-gallery">
                           {selectedRequest.images && selectedRequest.images.map((img, idx) => (
                              <div key={idx} className="gallery-item" onClick={() => { setActiveImageIndex(idx); setIsLightboxOpen(true); }}>
                                 <img src={img} alt={`car-${idx}`} />
                                 <div className="img-overlay"><ExternalLink size={14} /></div>
                              </div>
                           ))}
                           {(!selectedRequest.images || selectedRequest.images.length === 0) && (
                              <div className="no-images">Fotoğraf yüklenmemiş.</div>
                           )}
                        </div>
                     </div>

                     <div className="detail-section">
                        <div className="section-title"><Zap size={16} /> Ekspertiz Durumu</div>
                        <div className="expertise-visual-box">
                           <div className="car-svg-container">
                              <ExpertiseMap expertise={selectedRequest.expertise_report} />
                           </div>
                           <div className="expertise-legend">
                              {[{l:'ORİJİNAL',c:COLOR_MAP.original},{l:'LOKAL',c:COLOR_MAP.local_painted},{l:'BOYALI',c:COLOR_MAP.painted},{l:'DEĞİŞEN',c:COLOR_MAP.changed}].map(s=>(
                                 <div key={s.l} className="legend-item">
                                    <div className="legend-dot" style={{ background: s.c }}></div>
                                    <span>{s.l}</span>
                                 </div>
                              ))}
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            <div className="detail-modal-footer">
               <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: 'var(--text-dim)' }}>DURUMU GÜNCELLE:</span>
                  <div className="status-button-group">
                     {['Bekliyor', 'İnceleniyor', 'Teklif Verildi', 'Satın Alındı', 'Reddedildi'].map(s => (
                        <button 
                          key={s} 
                          className={`status-btn ${selectedRequest.status === s ? 'active' : ''}`}
                          onClick={() => handleStatusUpdate(selectedRequest.id, s)}
                        >
                           {s}
                        </button>
                     ))}
                  </div>
               </div>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX / CAROUSEL ORIENTED */}
      {isLightboxOpen && selectedRequest && (
        <div className="lightbox-overlay" onClick={() => setIsLightboxOpen(false)}>
           <button className="lightbox-close" onClick={() => setIsLightboxOpen(false)}><X size={32} /></button>
           
           <div className="lightbox-content" onClick={e => e.stopPropagation()}>
              <button 
                className="lightbox-nav prev" 
                onClick={() => setActiveImageIndex((activeImageIndex - 1 + selectedRequest.images.length) % selectedRequest.images.length)}
                disabled={selectedRequest.images.length <= 1}
              >
                 <ChevronLeft size={48} />
              </button>

              <div className="lightbox-image-container">
                 <img src={selectedRequest.images[activeImageIndex]} alt="carousel" />
                 <div className="lightbox-counter">{activeImageIndex + 1} / {selectedRequest.images.length}</div>
              </div>

              <button 
                className="lightbox-nav next" 
                onClick={() => setActiveImageIndex((activeImageIndex + 1) % selectedRequest.images.length)}
                disabled={selectedRequest.images.length <= 1}
              >
                 <ChevronRight size={48} />
              </button>
           </div>
        </div>
      )}

      <style>{`
        .admin-sell-requests {
          padding: 1rem;
          animation: fadeIn 0.5s ease-out;
        }

        .btn-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-icon:hover {
          transform: translateY(-2px);
          filter: brightness(1.2);
        }

        /* Modal Styles */
        .detail-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(2, 6, 23, 0.9);
          backdrop-filter: blur(12px);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .detail-modal-content {
          background: #0f172a;
          width: 100%;
          max-width: 1100px;
          max-height: 90vh;
          border-radius: 2.5rem;
          border: 1px solid rgba(255,255,255,0.1);
          box-shadow: 0 50px 100px -20px rgba(0,0,0,0.5);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: modalSlide 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes modalSlide {
           from { transform: translateY(30px) scale(0.95); opacity: 0; }
           to { transform: translateY(0) scale(1); opacity: 1; }
        }

        .detail-modal-header {
          padding: 2rem 2.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-icon-box {
          width: 54px;
          height: 54px;
          background: rgba(0, 209, 255, 0.1);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn {
          background: rgba(255,255,255,0.05);
          border: none;
          color: white;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-btn:hover { background: rgba(239, 68, 68, 0.2); color: #ef4444; }

        .detail-modal-body {
          padding: 2.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 3rem;
        }

        @media (max-width: 992px) {
           .detail-grid { grid-template-columns: 1fr; }
        }

        .section-title {
          font-size: 0.9rem;
          font-weight: 900;
          color: white;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          margin-bottom: 1.2rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .detail-section {
           margin-bottom: 2.5rem;
        }

        .info-card {
           background: rgba(255,255,255,0.02);
           border-radius: 1.5rem;
           padding: 1.5rem;
           border: 1px solid rgba(255,255,255,0.05);
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          font-size: 0.9rem;
        }

        .info-row:last-child { border-bottom: none; }
        .info-row span { color: var(--text-dim); }
        .info-row strong { color: white; font-weight: 700; }

        .note-card {
           color: var(--text-dim);
           line-height: 1.6;
           font-style: italic;
           font-size: 0.9rem;
        }

        .image-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          gap: 1rem;
        }

        .gallery-item {
          aspect-ratio: 4/3;
          border-radius: 0.75rem;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          border: 1px solid rgba(255,255,255,0.1);
        }

        .gallery-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s; }
        .gallery-item:hover img { transform: scale(1.1); }

        .img-overlay {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 209, 255, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
          color: white;
        }
        .gallery-item:hover .img-overlay { opacity: 1; }

        .expertise-visual-box {
           background: rgba(255,255,255,0.02);
           border-radius: 2rem;
           padding: 2rem;
           border: 1px solid rgba(255,255,255,0.05);
           display: flex;
           align-items: center;
           gap: 2rem;
        }

        .car-svg-container {
           flex: 1;
           max-width: 200px;
        }

        .expertise-legend {
           display: flex;
           flex-direction: column;
           gap: 0.75rem;
        }

        .legend-item {
           display: flex;
           align-items: center;
           gap: 0.6rem;
           font-size: 0.75rem;
           font-weight: 700;
           color: var(--text-dim);
        }

        .legend-dot { width: 10px; height: 10px; border-radius: 3px; }

        .detail-modal-footer {
          padding: 1.5rem 2.5rem;
          background: rgba(255,255,255,0.02);
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .status-button-group {
           display: flex;
           gap: 0.5rem;
           flex-wrap: wrap;
        }

        .status-btn {
           background: rgba(255,255,255,0.05);
           border: 1px solid rgba(255,255,255,0.1);
           color: var(--text-dim);
           padding: 0.6rem 1rem;
           border-radius: 0.75rem;
           font-size: 0.75rem;
           font-weight: 700;
           cursor: pointer;
           transition: all 0.2s;
        }

        .status-btn:hover { background: rgba(255,255,255,0.1); }

        .status-btn.active {
           background: var(--primary);
           color: black;
           border-color: var(--primary);
           box-shadow: 0 4px 15px rgba(0, 209, 255, 0.3);
        }

        /* Lightbox Styles */
        .lightbox-overlay {
           position: fixed;
           top: 0; left: 0; right: 0; bottom: 0;
           background: rgba(0,0,0,0.95);
           backdrop-filter: blur(20px);
           z-index: 10000;
           display: flex;
           align-items: center;
           justify-content: center;
           animation: fadeIn 0.3s ease;
        }

        .lightbox-close {
           position: absolute;
           top: 30px; right: 30px;
           background: none; border: none; color: white;
           cursor: pointer; opacity: 0.6; transition: opacity 0.2s;
        }
        .lightbox-close:hover { opacity: 1; }

        .lightbox-content {
           display: flex;
           align-items: center;
           gap: 2rem;
           width: 90%;
           height: 80%;
        }

        .lightbox-image-container {
           flex: 1;
           height: 100%;
           display: flex;
           flex-direction: column;
           align-items: center;
           justify-content: center;
           position: relative;
        }

        .lightbox-image-container img {
           max-width: 100%;
           max-height: 100%;
           object-fit: contain;
           border-radius: 1rem;
           box-shadow: 0 30px 60px rgba(0,0,0,0.5);
           animation: slideIn 0.4s ease-out;
        }

        @keyframes slideIn {
           from { opacity: 0; transform: scale(0.9); }
           to { opacity: 1; transform: scale(1); }
        }

        .lightbox-nav {
           background: rgba(255,255,255,0.05);
           border: 1px solid rgba(255,255,255,0.1);
           color: white;
           width: 80px;
           height: 80px;
           border-radius: 50%;
           display: flex;
           align-items: center;
           justify-content: center;
           cursor: pointer;
           transition: all 0.3s;
        }

        .lightbox-nav:hover:not(:disabled) {
           background: var(--primary);
           color: black;
           transform: scale(1.1);
        }

        .lightbox-nav:disabled { opacity: 0.1; cursor: not-allowed; }

        .lightbox-counter {
           margin-top: 1.5rem;
           background: rgba(255,255,255,0.1);
           padding: 0.5rem 1.5rem;
           border-radius: 2rem;
           font-weight: 800;
           font-size: 0.9rem;
           color: white;
        }

        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

const ExpertiseMap = ({ expertise = {} }) => {
  if (!expertise) expertise = {};
  
  const StaticPart = ({ partKey, d }) => {
    const status = expertise[partKey];
    return (
      <path 
        d={d} 
        fill={COLOR_MAP[status] || COLOR_MAP.original} 
        stroke="rgba(255,255,255,0.15)" 
        strokeWidth="2"
        className="transition-all duration-300"
      />
    );
  };

  return (
    <svg viewBox="0 0 500 650" style={{ width: '100%', height: 'auto' }}>
      <g transform="translate(250, 20)">
        <StaticPart partKey="front_bumper" d="M -50,15 L 50,15 L 50,30 Q 50,35 45,35 L -45,35 Q -50,35 -50,30 Z" />
        <StaticPart partKey="hood" d="M -55,45 Q 0,35 55,45 L 70,165 Q 0,180 -70,165 Z" />
        <StaticPart partKey="roof" d="M -85,235 Q 0,250 85,235 L 90,420 Q 0,440 -90,420 Z" />
        <StaticPart partKey="trunk" d="M -80,480 Q 0,500 80,480 L 70,590 Q 0,610 -70,590 Z" />
        <StaticPart partKey="rear_bumper" d="M -55,600 L 55,600 L 55,620 Q 55,630 45,630 L -45,630 Q -55,630 -55,620 Z" />
        
        <StaticPart partKey="front_left_fender" d="M -100,55 L -160,70 Q -185,80 -185,120 L -185,170 L -110,170 Z" />
        <StaticPart partKey="front_left_door" d="M -115,185 L -200,230 L -210,330 L -120,320 Z" />
        <StaticPart partKey="rear_left_door" d="M -130,340 L -215,360 L -220,480 L -135,480 Z" />
        <StaticPart partKey="rear_left_fender" d="M -135,495 L -225,495 Q -235,560 -210,590 L -120,610 Z" />
        
        <StaticPart partKey="front_right_fender" d="M 100,55 L 160,70 Q 185,80 185,120 L 185,170 L 110,170 Z" />
        <StaticPart partKey="front_right_door" d="M 115,185 L 200,230 L 210,330 L 120,320 Z" />
        <StaticPart partKey="rear_right_door" d="M 130,340 L 215,360 L 220,480 L 135,480 Z" />
        <StaticPart partKey="rear_right_fender" d="M 135,495 L 225,495 Q 235,560 210,590 L 120,610 Z" />
      </g>
    </svg>
  );
};

export default SellRequests;
